import axios from "axios";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const ManProductDetails = () => {
  const { id } = useParams(); // Extract 'id' from URL parameters
  const [product, setProduct] = useState(null); // Store product details
  const [error, setError] = useState(""); // Store error message
  const [mainImage, setMainImage] = useState(""); // Store main image for display
  const [activeSection, setActiveSection] = useState(null); // Manage expandable sections

  const URL = process.env.REACT_APP_URL; // Base API URL

  // Fetch product details when 'id' changes
  useEffect(() => {
    axios
      .get(`${URL}products/${id}`)
      .then((response) => {
        setProduct(response.data);
        setMainImage(`${URL}upload/${response.data.image1 || "default.jpg"}`);
        toggleSection("description");
        setError("");
      })
      .catch((error) => {
        if (error.response && error.response.status === 404) {
          setError("Product not found.");
        } else {
          setError("Error fetching product details.");
        }
      });
  }, [id, URL]);

  // Toggle the visibility of a section
  const toggleSection = (section) => {
    setActiveSection(activeSection === section ? null : section);
  };

  if (error) {
    return <p className="text-center mt-5 text-danger">{error}</p>;
  }

  if (!product) {
    return <p className="text-center mt-5">Loading product details...</p>;
  }
  const imageArray = [product.image1, product.image2, product.image3].filter(
    (image) => image && image.trim() !== "File upload error" // Ensures non-empty, valid images
  );
  return (
    <div className="container mt-5 mb-5">
      <style>
        {`.image-container {
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 100%;
  height: 45vh; /* Fixed height for main image */
  overflow: hidden;
}

.main-image {
  width: auto;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.thumbnail-image {
  width: 60px;
  height: 60px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s;
}

.thumbnail-image:hover {
  transform: scale(1.1);
}
`}
      </style>

      <div className="row">
        {/* Left: Product Images */}
        <div className="col-md-6">
          <div className="row">
            {/* Main Image Column */}
            <div className="col-12 col-lg-3 text-center justify-content-center">
              {imageArray.length > 0 && (
                <div className="d-flex flex-column align-items-center">
                  {imageArray.map((image, index) => (
                    <img
                      key={index}
                      src={`${URL}upload/${image}`}
                      alt={`Thumbnail ${index + 1}`}
                      className="thumbnail-image mb-2"
                      onClick={() => setMainImage(`${URL}upload/${image}`)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Thumbnail Images Column */}
            <div className="col-12 col-lg-9 d-flex justify-content-center flex-column align-items-center">
              <div className="image-container">
                <img
                  src={mainImage}
                  alt={product.name}
                  className="main-image"
                />
              </div>
            </div>
          </div>

          {/* Video Section */}
          {product.video && (
            <div className="mt-4">
              {/* <h5>Product Video</h5> */}
              <div className="embed-responsive embed-responsive-16by9">
                <video
                  width="100%"
                  height="400"
                  controls
                  controlsList="nodownload"
                  onContextMenu={(e) => e.preventDefault()}
                >
                  <source
                    src={`${URL}upload/${product.video}`}
                    type="video/mp4"
                  />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          )}
        </div>

        {/* Right: Product Details */}
        <div className="col-md-6 mt-4">
          <h3>Product: {product.name}</h3>

          {/* Description Section */}
          <div className="mt-4">
            <h5
              className="d-flex justify-content-between align-items-center"
              onClick={() => toggleSection("description")}
              style={{
                cursor: "pointer",
                borderBottom: "1px solid #ddd",
                paddingBottom: "5px",
              }}
            >
              Description
              <span>{activeSection === "description" ? "-" : "+"}</span>
            </h5>
            {activeSection === "description" && (
              <p className="mt-2 text-muted">
                {product.description || "No description available."}
              </p>
            )}
          </div>

          {/* Product Details Section */}
          <div className="mt-4">
            <h5
              className="d-flex justify-content-between align-items-center"
              onClick={() => toggleSection("details")}
              style={{
                cursor: "pointer",
                borderBottom: "1px solid #ddd",
                paddingBottom: "5px",
              }}
            >
              Product Details
              <span>{activeSection === "details" ? "-" : "+"}</span>
            </h5>
            {activeSection === "details" && (
              <ul className="mt-2 text-muted">
                <li>
                  <strong>Category:</strong> {product.category || "N/A"}
                </li>
                <li>
                  {product.grossWeight ? (
                    <>
                      <strong>Gross Weight:</strong> <span> {product.grossWeight} gm</span>
                    </>
                  ) : null}
                </li>
                {product.goldKt ? (
                  <li>
                    <strong>Gold KT:</strong> <span>{product.goldKt} KT</span>
                  </li>
                ) : null}
                {product.goldColor ? (
                  <li>
                    <strong>Gold Color:</strong>{" "}
                    <span>{product.goldColor}</span>
                  </li>
                ) : null}
           {product.diamondWeight ? (
                <li>
                  <strong>Diamond Weight:</strong>{" "}
                    <span>{product.diamondWeight} CT</span>
                </li>
                  ) : null}
                <li>
                  <strong>Diamond Color & Clarity:</strong>{" "}
                  {product.diamondColorAndClarity}
                </li>
                {product.height || product.width || product.length ? (
                <li>
                  <strong>Dimensions:</strong>
                  {product.height  ? (
                    <span> Height : {product.height} mm </span>
                  ) : null}

                  {product.width ? (
                    <span>|| Width : {product.width} mm </span>
                  ) : null}
                  {product.length ? (
                    <span>|| Length: {product.length} mm </span>
                  ) : null}
                </li>
                ): null 
                }
                <li>
                  <strong>Product ID:</strong> {product.productId}
                </li>
              </ul>
            )}
          </div>

          {/* WhatsApp Inquiry Button */}
          <div className="mt-4">
            <a
              href={`https://api.whatsapp.com/send?phone=919920897971&text=Hello,%20I%20am%20interested%20in%20this%20ProductID%20${encodeURIComponent(
                product.productId
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-success"
            >
              <i className="bi bi-whatsapp"></i> Inquire on WhatsApp
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManProductDetails;
